import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { cardsAPI } from '../api/endpoints/cards'
import { toast } from '../store'
import { CardRequest, MoveCardRequest } from '../types'
import { handleConflict, isConflictError } from '../utils/conflictHandler'

/**
 * Custom hooks for card operations
 * Implements Requirements 7.1, 7.2, 7.3, 7.4, 7.5, 8.1, 8.2, 9.1, 9.2, 9.3
 */

/**
 * Get all cards in a column
 * Requirement 7.1: Card creation adds at end position
 * OPTIMIZED: Aggressive caching for better performance
 */
export const useCards = (columnId: number) => {
  return useQuery({
    queryKey: ['columns', columnId, 'cards'],
    queryFn: () => cardsAPI.getByColumn(columnId),
    enabled: !!columnId,
    staleTime: 30000, // Consider data fresh for 30 seconds
    gcTime: 5 * 60 * 1000, // Keep in cache for 5 minutes
    refetchOnWindowFocus: false, // Don't refetch on window focus
    refetchOnMount: false, // Don't refetch on component mount if data exists
  })
}

/**
 * Get card by ID
 * Requirement 7.2: Card view displays all details
 */
export const useCard = (id: number) => {
  return useQuery({
    queryKey: ['cards', id],
    queryFn: () => cardsAPI.getById(id),
    enabled: !!id,
  })
}

/**
 * Create a new card
 * Requirement 7.1: Card creation adds at end position
 * Requirement 13.1: Optimistic updates for immediate UI feedback
 */
export const useCreateCard = (columnId: number) => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (data: CardRequest) => cardsAPI.create(columnId, data),
    onMutate: async (newCard) => {
      // Cancel outgoing refetches
      await queryClient.cancelQueries({
        queryKey: ['columns', columnId, 'cards'],
      })

      // Snapshot previous value
      const previousCards = queryClient.getQueryData([
        'columns',
        columnId,
        'cards',
      ])

      // Optimistically update to the new value
      queryClient.setQueryData(['columns', columnId, 'cards'], (old: any) => {
        if (!old) return old

        // Create optimistic card with temporary ID
        const optimisticCard = {
          id: Date.now(), // Temporary ID
          title: newCard.title,
          description: newCard.description || '',
          columnId,
          archived: false,
          position: old.length,
          members: [],
          comments: [],
          labels: [],
          attachments: [],
          createdAt: new Date().toISOString(),
          updatedAt: new Date().toISOString(),
        }

        return [...old, optimisticCard]
      })

      return { previousCards }
    },
    onError: (error: any, _variables, context) => {
      // Requirement 13.2: Revert optimistic update on error
      if (context?.previousCards) {
        queryClient.setQueryData(
          ['columns', columnId, 'cards'],
          context.previousCards
        )
      }

      // Requirement 13.4: Handle conflicts
      if (isConflictError(error)) {
        handleConflict(
          queryClient,
          [['columns', columnId, 'cards']],
          'Card creation conflict detected.'
        )
      } else {
        toast.error(error.message || 'Failed to create card')
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ['columns', columnId, 'cards'],
      })
      toast.success('Card created successfully')
    },
  })
}

/**
 * Update card details
 * Requirement 7.3: Card updates reflect immediately
 * Requirement 13.1: Optimistic updates for immediate UI feedback
 */
export const useUpdateCard = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ id, data }: { id: number; data: CardRequest }) =>
      cardsAPI.update(id, data),
    onMutate: async ({ id, data }) => {
      // Cancel outgoing refetches
      await queryClient.cancelQueries({ queryKey: ['cards', id] })

      // Snapshot previous value
      const previousCard = queryClient.getQueryData(['cards', id])

      // Optimistically update the card
      queryClient.setQueryData(['cards', id], (old: any) => {
        if (!old) return old
        return {
          ...old,
          ...data,
          updatedAt: new Date().toISOString(),
        }
      })

      // Also update in column cards list if present
      const columnQueries = queryClient.getQueriesData({
        queryKey: ['columns'],
      })
      const previousColumnData: any[] = []

      columnQueries.forEach(([queryKey, data]) => {
        if (Array.isArray(data)) {
          previousColumnData.push({ queryKey, data })
          queryClient.setQueryData(queryKey, (old: any) => {
            if (!Array.isArray(old)) return old
            return old.map((card: any) =>
              card.id === id
                ? { ...card, ...data, updatedAt: new Date().toISOString() }
                : card
            )
          })
        }
      })

      return { previousCard, previousColumnData }
    },
    onError: (error: any, variables, context) => {
      // Requirement 13.2: Revert optimistic update on error
      if (context?.previousCard) {
        queryClient.setQueryData(['cards', variables.id], context.previousCard)
      }
      if (context?.previousColumnData) {
        context.previousColumnData.forEach(({ queryKey, data }) => {
          queryClient.setQueryData(queryKey, data)
        })
      }

      // Requirement 13.4: Handle conflicts
      if (isConflictError(error)) {
        handleConflict(
          queryClient,
          [['cards', variables.id], ['columns']],
          'Card update conflict detected.'
        )
      } else {
        toast.error(error.message || 'Failed to update card')
      }
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['cards', variables.id] })
      queryClient.invalidateQueries({ queryKey: ['columns'] })
      toast.success('Card updated successfully')
    },
  })
}

/**
 * Move card to different column or position
 * Requirement 8.1: Card drag within column updates positions
 * Requirement 8.2: Card drag between columns updates both columns
 * Requirement 13.1: Optimistic updates for immediate UI feedback
 *
 * OPTIMIZED: Instant UI updates with smart cache management
 */
export const useMoveCard = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ id, data }: { id: number; data: MoveCardRequest }) =>
      cardsAPI.move(id, data),
    onMutate: async ({ id, data }) => {
      const targetColumnId = data.targetColumnId || data.columnId
      const targetPosition = data.newPosition ?? data.position ?? 0

      // Cancel outgoing refetches for affected columns only
      await queryClient.cancelQueries({ queryKey: ['columns'] })

      // Snapshot all column data
      const columnQueries = queryClient.getQueriesData({
        queryKey: ['columns'],
      })
      const previousColumnData: any[] = []

      let sourceColumnId: number | null = null
      let movedCard: any = null

      // First pass: find the card and its source column
      columnQueries.forEach(([queryKey, queryData]) => {
        if (Array.isArray(queryData)) {
          previousColumnData.push({ queryKey, data: queryData })

          const cardIndex = queryData.findIndex((c: any) => c.id === id)
          if (cardIndex !== -1) {
            movedCard = { ...queryData[cardIndex] }
            sourceColumnId = movedCard.columnId
          }
        }
      })

      if (!movedCard || !targetColumnId) {
        return { previousColumnData }
      }

      // Second pass: update affected columns
      columnQueries.forEach(([queryKey, queryData]) => {
        if (!Array.isArray(queryData)) return

        const columnIdMatch = queryKey.toString().match(/columns,(\d+),cards/)
        if (!columnIdMatch) return

        const currentColumnId = parseInt(columnIdMatch[1])

        // Update source column (remove card)
        if (
          currentColumnId === sourceColumnId &&
          sourceColumnId !== targetColumnId
        ) {
          queryClient.setQueryData(queryKey, (old: any) => {
            if (!Array.isArray(old)) return old
            const filtered = old.filter((c: any) => c.id !== id)
            // Recalculate positions
            return filtered.map((c: any, index: number) => ({
              ...c,
              position: index,
            }))
          })
        }
        // Update target column (add card)
        else if (currentColumnId === targetColumnId) {
          queryClient.setQueryData(queryKey, (old: any) => {
            if (!Array.isArray(old)) return old

            // Remove card if it exists (for same column moves)
            let cards = old.filter((c: any) => c.id !== id)

            // Update moved card
            const updatedCard = {
              ...movedCard,
              columnId: targetColumnId,
              position: targetPosition,
              updatedAt: new Date().toISOString(),
            }

            // Insert at target position
            cards.splice(targetPosition, 0, updatedCard)

            // Recalculate all positions
            return cards.map((c: any, index: number) => ({
              ...c,
              position: index,
            }))
          })
        }
      })

      return { previousColumnData, sourceColumnId, targetColumnId }
    },
    onError: (error: any, _variables, context) => {
      // Requirement 13.2: Revert optimistic update on error
      if (context?.previousColumnData) {
        context.previousColumnData.forEach(({ queryKey, data }) => {
          queryClient.setQueryData(queryKey, data)
        })
      }

      // Requirement 13.4: Handle conflicts
      if (isConflictError(error)) {
        handleConflict(
          queryClient,
          [['columns']],
          'Card move conflict detected. Positions have been refreshed.'
        )
      } else {
        toast.error('Failed to move card')
      }
    },
    onSettled: (_data, _error, variables, context) => {
      // Only invalidate affected columns, not all columns
      const targetColumnId =
        variables.data.targetColumnId || variables.data.columnId
      const sourceColumnId = context?.sourceColumnId

      if (sourceColumnId) {
        queryClient.invalidateQueries({
          queryKey: ['columns', sourceColumnId, 'cards'],
          refetchType: 'none', // Don't refetch immediately
        })
      }

      if (targetColumnId && targetColumnId !== sourceColumnId) {
        queryClient.invalidateQueries({
          queryKey: ['columns', targetColumnId, 'cards'],
          refetchType: 'none', // Don't refetch immediately
        })
      }

      // Refetch in background after a delay to sync with server
      setTimeout(() => {
        if (sourceColumnId) {
          queryClient.refetchQueries({
            queryKey: ['columns', sourceColumnId, 'cards'],
          })
        }
        if (targetColumnId && targetColumnId !== sourceColumnId) {
          queryClient.refetchQueries({
            queryKey: ['columns', targetColumnId, 'cards'],
          })
        }
      }, 500)
    },
  })
}

/**
 * Archive or unarchive a card
 * Requirement 7.4: Card archival hides from board
 * Requirement 13.1: Optimistic updates for immediate UI feedback
 */
export const useArchiveCard = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ id, archived }: { id: number; archived: boolean }) =>
      cardsAPI.archive(id, archived),
    onMutate: async ({ id, archived }) => {
      // Cancel outgoing refetches
      await queryClient.cancelQueries({ queryKey: ['cards', id] })
      await queryClient.cancelQueries({ queryKey: ['columns'] })

      // Snapshot previous values
      const previousCard = queryClient.getQueryData(['cards', id])
      const columnQueries = queryClient.getQueriesData({
        queryKey: ['columns'],
      })
      const previousColumnData: any[] = []

      // Update card
      queryClient.setQueryData(['cards', id], (old: any) => {
        if (!old) return old
        return { ...old, archived }
      })

      // Update in column lists
      columnQueries.forEach(([queryKey, data]) => {
        if (Array.isArray(data)) {
          previousColumnData.push({ queryKey, data })
          queryClient.setQueryData(queryKey, (old: any) => {
            if (!Array.isArray(old)) return old
            // If archiving, remove from list; if unarchiving, keep it
            if (archived) {
              return old.filter((card: any) => card.id !== id)
            }
            return old.map((card: any) =>
              card.id === id ? { ...card, archived } : card
            )
          })
        }
      })

      return { previousCard, previousColumnData }
    },
    onError: (error: any, variables, context) => {
      // Requirement 13.2: Revert optimistic update on error
      if (context?.previousCard) {
        queryClient.setQueryData(['cards', variables.id], context.previousCard)
      }
      if (context?.previousColumnData) {
        context.previousColumnData.forEach(({ queryKey, data }) => {
          queryClient.setQueryData(queryKey, data)
        })
      }

      // Requirement 13.4: Handle conflicts
      if (isConflictError(error)) {
        handleConflict(
          queryClient,
          [['cards', variables.id], ['columns']],
          'Card archive conflict detected.'
        )
      } else {
        toast.error(error.message || 'Failed to archive card')
      }
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['cards', variables.id] })
      queryClient.invalidateQueries({ queryKey: ['columns'] })
      toast.success(variables.archived ? 'Card archived' : 'Card unarchived')
    },
  })
}

/**
 * Delete a card
 * Requirement 7.5: Card deletion removes all associated data
 * Requirement 13.1: Optimistic updates for immediate UI feedback
 */
export const useDeleteCard = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: cardsAPI.delete,
    onMutate: async (id) => {
      // Cancel outgoing refetches
      await queryClient.cancelQueries({ queryKey: ['columns'] })

      // Snapshot all column data
      const columnQueries = queryClient.getQueriesData({
        queryKey: ['columns'],
      })
      const previousColumnData: any[] = []

      // Optimistically remove card from all column lists
      columnQueries.forEach(([queryKey, data]) => {
        if (Array.isArray(data)) {
          previousColumnData.push({ queryKey, data })
          queryClient.setQueryData(queryKey, (old: any) => {
            if (!Array.isArray(old)) return old
            return old.filter((card: any) => card.id !== id)
          })
        }
      })

      return { previousColumnData }
    },
    onError: (error: any, _variables, context) => {
      // Requirement 13.2: Revert optimistic update on error
      if (context?.previousColumnData) {
        context.previousColumnData.forEach(({ queryKey, data }) => {
          queryClient.setQueryData(queryKey, data)
        })
      }

      // Requirement 13.4: Handle conflicts
      if (isConflictError(error)) {
        handleConflict(
          queryClient,
          [['columns']],
          'Card deletion conflict detected.'
        )
      } else {
        toast.error(error.message || 'Failed to delete card')
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['columns'] })
      toast.success('Card deleted successfully')
    },
  })
}

/**
 * Assign member to card
 * Requirement 9.1: Member assignment displays avatar
 */
export const useAssignCardMember = (cardId: number) => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (userId: number) => cardsAPI.assignMember(cardId, userId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['cards', cardId] })
      queryClient.invalidateQueries({ queryKey: ['columns'] })
      toast.success('Member assigned to card')
    },
    onError: (error: any) => {
      toast.error(error.message || 'Failed to assign member')
    },
  })
}

/**
 * Unassign member from card
 * Requirement 9.2: Member unassignment removes from card
 */
export const useUnassignCardMember = (cardId: number) => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (userId: number) => cardsAPI.unassignMember(cardId, userId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['cards', cardId] })
      queryClient.invalidateQueries({ queryKey: ['columns'] })
      toast.success('Member unassigned from card')
    },
    onError: (error: any) => {
      toast.error(error.message || 'Failed to unassign member')
    },
  })
}
